import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Trash2, X } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { contactsService } from "@/services/contactsService";
import { scheduleContactPermanentDelete } from "@/lib/deferredContactDelete";
import { getApiErrorMessage } from "@/utils/apiErrors";
import type { ContactDeletionImpact } from "@/types/contactDeletion";

interface Props {
  open: boolean;
  /** Contacts to delete. One id and many ids take the same path. */
  contactIds: string[];
  /** Fallback names for the header, used until the preview returns its own. */
  contactLabels?: Record<string, string>;
  onClose: () => void;
  /** Called once the delete succeeds, with the report of what was removed. */
  onDeleted: (impact: ContactDeletionImpact) => void;
  /** Called when the undo window starts — hide rows optimistically. */
  onDeleteScheduled?: (contactIds: string[]) => void;
  /** Called when the user undoes — restore hidden rows. */
  onDeleteUndo?: (contactIds: string[]) => void;
}

type Phase = "loading" | "confirm" | "deleting" | "error";

/**
 * Pulls the server's message off a rejected request.
 *
 * The api client rejects with a plain `ApiError` object, not an `Error`, so the usual
 * `e instanceof Error` check silently discards every backend message — including
 * "this contact is linked to a CRM and cannot be deleted", which is the one the
 * operator most needs to see.
 */
function errorMessage(e: unknown, fallback: string): string {
  return getApiErrorMessage(e) ?? (e instanceof Error ? e.message : fallback);
}

/**
 * Two-phase permanent delete: fetch the dry run, show what will disappear, and only
 * call the delete once the operator confirms that report.
 *
 * The preview is re-fetched every time the dialog opens rather than cached — a stale
 * count would be a promise the delete cannot keep.
 */
export function ContactDeleteDialog({
  open,
  contactIds,
  contactLabels,
  onClose,
  onDeleted,
  onDeleteScheduled,
  onDeleteUndo,
}: Props) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [impact, setImpact] = useState<ContactDeletionImpact | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Callers often pass `contactIds={[id]}` inline — a new array every render would
  // re-run the preview and re-open the global 403 modal in a loop after dismiss.
  const contactIdsKey = useMemo(
    () => [...contactIds].sort().join(","),
    [contactIds.join(",")],
  );

  useEffect(() => {
    if (!open || contactIdsKey.length === 0) return;

    const ids = contactIdsKey.split(",").filter(Boolean);
    let cancelled = false;
    setPhase("loading");
    setImpact(null);
    setError(null);

    contactsService
      .previewDeletion(ids)
      .then((result) => {
        if (cancelled) return;
        setImpact(result);
        setPhase("confirm");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(errorMessage(e, "Could not calculate what would be deleted."));
        setPhase("error");
      });

    // The dialog can be closed mid-request; ignore a preview that lands afterwards
    // so it cannot flip a closed dialog back into the confirm state.
    return () => {
      cancelled = true;
    };
  }, [open, contactIdsKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape must not abandon an in-flight delete — the request continues either way.
      if (e.key === "Escape" && phase !== "deleting") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, phase]);

  if (!open) return null;

  const names = { ...(contactLabels ?? {}), ...(impact?.contactNames ?? {}) };

  const runDelete = () => {
    const blockedSet = new Set(impact?.blockedContactIds ?? []);
    const ids = (impact?.contactIds ?? contactIds).filter((id) => !blockedSet.has(id));
    if (ids.length === 0) return;

    const label =
      ids.length === 1 ? (names[ids[0]] ?? "this contact") : `${ids.length} contacts`;

    onClose();
    scheduleContactPermanentDelete({
      contactIds: ids,
      label,
      onScheduled: () => onDeleteScheduled?.(ids),
      onUndo: () => onDeleteUndo?.(ids),
      onFailed: () => onDeleteUndo?.(ids),
      onDeleted: (impact: ContactDeletionImpact) => onDeleted(impact),
    });
  };

  const targetIds = impact?.contactIds ?? contactIds;
  const heading =
    targetIds.length === 1
      ? `Delete ${names[targetIds[0]] ?? "this contact"}?`
      : `Delete ${targetIds.length} contacts?`;

  const warnings = Object.entries(impact?.warnings ?? {});
  const busy = phase === "loading" || phase === "deleting";
  const blocked = impact?.blockedContactIds ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => !busy && onClose()}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        className="relative mx-4 flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-lg"
      >
        <div className="flex items-start justify-between gap-4 border-b border-bridge-border/60 px-6 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 shrink-0 rounded-full bg-red-50 p-2 text-bridge-danger">
              <AlertTriangle size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-bridge-fg">{heading}</h2>
              <p className="mt-0.5 text-xs text-bridge-muted">
                This permanently removes the contact and everything below. It cannot be undone.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="shrink-0 rounded-lg p-1.5 text-bridge-muted transition-colors hover:text-bridge-fg disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
          {phase === "loading" && (
            <div className="flex items-center gap-2 py-6 text-sm text-bridge-muted">
              <Loader2 size={16} className="animate-spin" />
              Working out what would be removed…
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-bridge-danger/30 bg-red-50 px-3 py-2 text-sm text-bridge-danger">
              {error}
            </div>
          )}

          {impact && phase !== "loading" && (
            <>
              {targetIds.length > 1 && (
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {targetIds.map((id) => names[id] ?? id).slice(0, 4).join(", ")}
                  {targetIds.length > 4 && ` and ${targetIds.length - 4} more`}
                </div>
              )}

              {/* The server sends exactly six lines, already ordered. Rendering them
                  verbatim keeps this component out of the business of deciding what
                  counts as significant. */}
              <ul className="divide-y divide-bridge-border/60 rounded-lg border border-bridge-border/60">
                {(impact.summary ?? []).map((item) => (
                  <li key={item.key} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <span
                      className={
                        item.count > 0
                          ? "tabular-nums text-sm font-semibold text-slate-800"
                          : "tabular-nums text-sm text-bridge-subtle"
                      }
                    >
                      {item.count.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>

              {blocked.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {blocked.length} selected contact{blocked.length === 1 ? " is" : "s are"} linked to
                  a CRM and will be skipped.
                </div>
              )}

              {impact.missingContactIds.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {impact.missingContactIds.length} selected contact
                  {impact.missingContactIds.length === 1 ? " no longer exists" : "s no longer exist"} and
                  will be skipped.
                </div>
              )}

              {warnings.length > 0 && (
                <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  {warnings.map(([subsystem, message]) => (
                    <p key={subsystem} className="text-sm text-amber-800">
                      <span className="font-semibold">{subsystem}:</span> {message}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-bridge-border/60 bg-gray-50 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={runDelete}
            loading={phase === "deleting"}
            // Only enabled once a preview has been shown — the confirmation is
            // meaningless if the operator has not seen what it covers.
            disabled={phase !== "confirm" || targetIds.length === 0}
          >
            <Trash2 size={14} />
            {targetIds.length === 1 ? "Delete permanently" : `Delete ${targetIds.length} permanently`}
          </Button>
        </div>
      </div>
    </div>
  );
}
